// Sticky readiness bar (ticket 86d385wc0, Phase 2).
//
// Always pinned at the foot of the SO detail panel while drafting. Headlines the
// total blocker count and renders one jump chip per section that needs attention
// (+ a read-only line-items reference chip). Clicking a chip routes to that
// section. It reads the SAME sectionStatus model the cards do, so it can never
// disagree with a card badge or the submit gate.

import { AlertCircle, CheckCircle2, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import { countBlockers, type SectionDescriptor, type SectionKey } from './sectionStatus';
import type { LineItemsReadiness } from './lineItemsStatus';

interface Props {
  sections: SectionDescriptor[];
  lineItems: LineItemsReadiness;
  onJumpSection: (key: SectionKey) => void;
  onJumpLineItems: () => void;
}

export function ReadinessBar({ sections, lineItems, onJumpSection, onJumpLineItems }: Props) {
  const sectionBlockers = countBlockers(sections);
  const lineBlocker = lineItems.blocker ? 1 : 0;
  const total = sectionBlockers + lineBlocker;
  const ready = total === 0;

  return (
    <div
      data-readiness-bar=""
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t bg-background px-4 py-2 text-sm"
    >
      {ready ? (
        <span className="flex items-center gap-2 font-medium text-success">
          <CheckCircle2 className="h-4 w-4" />
          Ready to submit
        </span>
      ) : (
        <span className="flex items-center gap-2 font-medium text-warning">
          <AlertCircle className="h-4 w-4" />
          {total} {total === 1 ? 'thing needs' : 'things need'} attention
        </span>
      )}

      {!ready && (
        <div className="flex flex-wrap items-center gap-1.5">
          {sections
            .filter((s) => s.blockers.length > 0)
            .map((s) => (
              <JumpChip
                key={s.key}
                label={s.title}
                count={s.blockers.length}
                onClick={() => onJumpSection(s.key)}
                title={`Go to ${s.title}: ${s.blockers.join(', ')}`}
              />
            ))}
          {lineItems.blocker && (
            <JumpChip
              icon
              label="Line items"
              onClick={onJumpLineItems}
              title={lineItems.blocker}
            />
          )}
        </div>
      )}
    </div>
  );
}

function JumpChip({
  label,
  count,
  onClick,
  title,
  icon,
}: {
  label: string;
  count?: number;
  onClick: () => void;
  title?: string;
  icon?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        'border-warning/30 bg-warning/10 text-warning hover:bg-warning/20',
      )}
    >
      {icon && <ClipboardList className="h-3 w-3" />}
      {label}
      {count != null && <span className="tabular-nums opacity-70">· {count}</span>}
    </button>
  );
}
